import { api } from './api';
import { chatTenantService } from './chatTenantService';
import { documentService } from './documentService';

export type DocumentScopeMode = 'bypass' | 'allowlist' | 'blocklist';

export interface WorkspacePromptLinkLabel {
  url: string;
  label: string;
}

// DTO principal do workspace
export interface WorkspaceDto {
  id: string;
  name: string;
  slug: string;
  assistantName: string;
  description?: string;
  systemPrompt?: string;
  rejectionMessage?: string;
  quickMessages: string[];
  avatar?: string;
  isActive: boolean;
  isPublic?: boolean;
  persona?: string;
  defaultLanguage?: string;
  aiTemperature?: number;
  aiHistory: number;
  similarityThreshold: number;
  chatProvider?: string;
  chatModel?: string;
  topN: number;
  chatMode: string;
  agentProvider?: string;
  agentModel?: string;
  vectorSearchMode?: string;
  embeddingProvider?: string;
  embeddingModel?: string;
  vectorDbProvider?: string;
  maxChunks: number;
  chunkSize: number;
  chunkOverlap: number;
  maxContextWindow: number;
  maxResponseTokens: number;
  accuracyPreference: string;
  accessType: string;
  documentScopeMode?: DocumentScopeMode;
  /** Pode ser usado em robô de atendimento externo (Know Copilot). */
  usableInExternalBot?: boolean;
  visionEnabled?: boolean;
  maxVisionImages?: number;
  maxVisionImageBytes?: number;
  visionDetail?: string;
  /** Nomes de botão para URLs do Prompt do Sistema. */
  promptLinkLabels?: WorkspacePromptLinkLabel[];
  /** Escrita: nova chave. Leitura: não vem preenchida. */
  apiKey?: string;
  hasApiKey?: boolean;
  chatApiBaseUrl?: string;
  embeddingApiBaseUrl?: string;
  embeddingApiKey?: string;
  hasEmbeddingApiKey?: boolean;
  tags: string[];
  providerSpecificSettings: Record<string, string>;
}

// DTO para criação de workspace
export interface CreateWorkspaceRequest {
  name: string;
  customerId?: string;
  description?: string;
  systemPrompt?: string;
  rejectionMessage?: string;
  quickMessages?: string[];
  avatar?: string;
  aiTemperature?: number;
  aiHistory?: number;
  similarityThreshold?: number;
  chatProvider?: string;
  chatModel?: string;
  topN?: number;
  chatMode?: string;
  agentProvider?: string;
  agentModel?: string;
  vectorSearchMode?: string;
  embeddingProvider?: string;
  embeddingModel?: string;
  vectorDbProvider?: string;
  maxChunks?: number;
  chunkSize?: number;
  chunkOverlap?: number;
  maxContextWindow?: number;
  maxResponseTokens?: number;
  accuracyPreference?: string;
  accessType?: string;
  apiKey?: string;
  tags?: string[];
  providerSpecificSettings?: Record<string, string>;
}

// DTO para atualização de workspace
export interface UpdateWorkspaceRequest {
  name?: string;
  description?: string;
  systemPrompt?: string;
  rejectionMessage?: string;
  quickMessages?: string[];
  avatar?: string;
  isActive?: boolean;
  aiTemperature?: number;
  aiHistory?: number;
  similarityThreshold?: number;
  chatProvider?: string;
  chatModel?: string;
  topN?: number;
  chatMode?: string;
  agentProvider?: string;
  agentModel?: string;
  vectorSearchMode?: string;
  embeddingProvider?: string;
  embeddingModel?: string;
  vectorDbProvider?: string;
  maxChunks?: number;
  chunkSize?: number;
  chunkOverlap?: number;
  maxContextWindow?: number;
  maxResponseTokens?: number;
  accuracyPreference?: string;
  accessType?: string;
  apiKey?: string;
  tags?: string[];
  providerSpecificSettings?: Record<string, string>;
}

// Resposta da API para listagem
export interface WorkspaceListResponse {
  workspaces: WorkspaceDto[];
}

/** Resumo retornado por GET /workspaces/chatindoc (agentes disponíveis no chat). */
export interface ChatInDocWorkspaceSummary {
  id: string;
  name: string;
  slug: string;
  assistantName: string;
  description: string;
  avatar?: string;
  isActive: boolean;
}

export interface WorkspaceForDocument extends ChatInDocWorkspaceSummary {
  isAccessible: boolean;
  restrictionReason: 'allowlist' | 'blocklist' | null;
}

// Serviço de workspace
class WorkspaceService {
  /**
   * Tenant selecionado por administrador do cliente proprietário para gerenciar workspaces
   * de outro cliente. Quando nulo, usa o tenant do usuário autenticado.
   */
  private activeTenantId: string | null = null;

  setActiveTenantId(tenantId: string | null): void {
    const normalized = tenantId?.trim();
    this.activeTenantId = normalized ? normalized : null;
  }

  getActiveTenantId(): string | null {
    return this.activeTenantId;
  }

  buildCustomerIdParams(): { customerId?: string } {
    if (!this.activeTenantId) {
      return {};
    }

    const storageCustomerId = this.getStorageCustomerId();
    if (storageCustomerId && this.activeTenantId === storageCustomerId) {
      return {};
    }

    return { customerId: this.activeTenantId };
  }

  private getStorageCustomerId(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    return (
      sessionStorage.getItem('@KnowInDoc:customer_id')?.trim() ||
      localStorage.getItem('@KnowInDoc:customer_id')?.trim() ||
      null
    );
  }

  private resolveTargetCustomerIdForRequest(explicitCustomerId?: string): string | undefined {
    const targetId = explicitCustomerId?.trim() || this.activeTenantId || this.getStorageCustomerId();
    const storageCustomerId = this.getStorageCustomerId();

    if (!targetId || (storageCustomerId && targetId === storageCustomerId)) {
      return undefined;
    }

    return targetId;
  }

  async getChatInDocWorkspaces(
    customerId?: string,
    options?: { skipFeatureUnavailableToast?: boolean },
  ): Promise<ChatInDocWorkspaceSummary[]> {
    try {
      const params = customerId
        ? { customerId }
        : chatTenantService.buildCustomerIdParams();

      const response = await api.get('/workspaces/chatindoc', {
        params,
        skipFeatureUnavailableToast: options?.skipFeatureUnavailableToast === true,
      });
      const raw = response.data;
      const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
      return list.map((w: any) => ({
        id: String(w.id),
        name: w.name ?? '',
        slug: w.slug ?? '',
        assistantName: (w.assistantName ?? w.name ?? 'Assistente') as string,
        description: (w.description ?? '') as string,
        avatar: typeof w.avatar === 'string' && w.avatar.trim() !== '' ? w.avatar : undefined,
        isActive: w.isActive !== false,
      }));
    } catch (error) {
      console.error('Erro ao buscar workspaces do ChatInDoc:', error);
      throw error;
    }
  }

  async getWorkspacesForDocument(
    documentId: string,
    options?: { skipFeatureUnavailableToast?: boolean },
  ): Promise<WorkspaceForDocument[]> {
    try {
      const response = await api.get(`/chatindoc/workspaces-for-document/${documentId}`, {
        params: documentService.buildCustomerIdParams(),
        skipFeatureUnavailableToast: options?.skipFeatureUnavailableToast === true,
      });
      const raw = response.data;
      const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
      return list.map((w: any) => ({
        id: String(w.id),
        name: w.name ?? '',
        slug: w.slug ?? '',
        assistantName: (w.assistantName ?? w.name ?? 'Assistente') as string,
        description: (w.description ?? '') as string,
        isActive: w.isActive !== false,
        isAccessible: w.isAccessible !== false,
        restrictionReason: w.restrictionReason === 'blocklist' || w.restrictionReason === 'allowlist'
          ? w.restrictionReason
          : null,
      }));
    } catch (error) {
      console.error('Erro ao buscar workspaces por documento:', error);
      throw error;
    }
  }

  async getWorkspaces(): Promise<WorkspaceDto[]> {
    try {
      const response = await api.get('/workspaces', {
        params: this.buildCustomerIdParams(),
      });
      
      if (response.data && Array.isArray(response.data)) {
        // API retornou array direto, convertendo para estrutura esperada
        return response.data.map((workspace: any) => ({
          id: workspace.id,
          name: workspace.name,
          slug: workspace.slug,
          assistantName: workspace.assistantName || workspace.name,
          description: workspace.description,
          systemPrompt: workspace.systemPrompt,
          rejectionMessage: workspace.rejectionMessage,
          quickMessages: workspace.quickMessages || [],
          avatar: workspace.avatar,
          isActive: workspace.isActive !== false,
          isPublic: workspace.isPublic === true,
          persona: workspace.persona,
          defaultLanguage: workspace.defaultLanguage,
          aiTemperature: workspace.aiTemperature,
          aiHistory: workspace.aiHistory || 10,
          similarityThreshold: workspace.similarityThreshold || 0.7,
          chatProvider: workspace.chatProvider,
          chatModel: workspace.chatModel || 'gpt-4o-mini',
          topN: workspace.topN || 5,
          chatMode: workspace.chatMode || 'chat',
          agentProvider: workspace.agentProvider,
          agentModel: workspace.agentModel,
          vectorSearchMode: workspace.vectorSearchMode,
          embeddingProvider: workspace.embeddingProvider,
          embeddingModel: workspace.embeddingModel || 'text-embedding-3-small',
          vectorDbProvider: workspace.vectorDbProvider,
          maxChunks: workspace.maxChunks || 10,
          chunkSize: workspace.chunkSize || 1000,
          chunkOverlap: workspace.chunkOverlap || 200,
          maxContextWindow: workspace.maxContextWindow || 4000,
          maxResponseTokens: workspace.maxResponseTokens || 2000,
          accuracyPreference: workspace.accuracyPreference || 'balanced', 
          accessType: workspace.accessType || 'public',
          documentScopeMode: workspace.documentScopeMode || 'bypass',
          usableInExternalBot: workspace.usableInExternalBot === true,
          apiKey: workspace.apiKey,
          tags: workspace.tags || [],
          providerSpecificSettings: workspace.providerSpecificSettings || {}
        }));
      }
      
      return [];
    } catch (error) {
      console.error('Erro ao buscar workspaces:', error);
      throw error;
    }
  }

  async getWorkspace(id: string): Promise<WorkspaceDto> {
    try {
      const response = await api.get(`/workspaces/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('[workspaceService.getWorkspace] Erro ao buscar workspace:', error);
      console.error('[workspaceService.getWorkspace] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  async getAvatarBlob(id: string): Promise<Blob> {
    const response = await api.get(`/workspaces/${id}/avatar`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async createWorkspace(data: CreateWorkspaceRequest, customerId?: string): Promise<WorkspaceDto> {
    try {
      const resolvedCustomerId = this.resolveTargetCustomerIdForRequest(customerId ?? data.customerId);
      const payload = {
        ...data,
        customerId: resolvedCustomerId,
      };

      const response = await api.post('/workspaces', payload);
      return response.data;
    } catch (error: any) {
      console.error('[workspaceService.createWorkspace] Erro na requisição:', error);
      console.error('[workspaceService.createWorkspace] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  async updateWorkspace(id: string, data: WorkspaceDto): Promise<WorkspaceDto> {
    try {
      const response = await api.put(`/workspaces`, data);
      return response.data;
    } catch (error: any) {
      console.error('[workspaceService.updateWorkspace] Erro na requisição:', error);
      console.error('[workspaceService.updateWorkspace] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  async duplicateWorkspace(
    id: string,
    sourceCustomerId?: string,
    targetCustomerId?: string
  ): Promise<WorkspaceDto> {
    try {
      const body: { sourceCustomerId?: string; targetCustomerId?: string } = {};

      const resolvedSourceCustomerId = this.resolveTargetCustomerIdForRequest(sourceCustomerId);
      if (resolvedSourceCustomerId) {
        body.sourceCustomerId = resolvedSourceCustomerId;
      }

      const resolvedTargetCustomerId = this.resolveTargetCustomerIdForRequest(targetCustomerId);
      if (resolvedTargetCustomerId) {
        body.targetCustomerId = resolvedTargetCustomerId;
      }

      const response = await api.post(`/workspaces/${id}/duplicate`, body);
      return response.data;
    } catch (error: any) {
      console.error('[workspaceService.duplicateWorkspace] Erro na requisição:', error);
      console.error('[workspaceService.duplicateWorkspace] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  async deleteWorkspace(id: string): Promise<void> {
    try {
      await api.delete(`/workspaces/${id}`);
    } catch (error: any) {
      console.error('[workspaceService.deleteWorkspace] Erro na requisição:', error);
      console.error('[workspaceService.deleteWorkspace] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  // Métodos para configurações específicas
  async getConfiguration(id: string): Promise<any> { // Assuming WorkspaceConfigurationDto is no longer needed
    try {
      const response = await api.get(`/workspaces/${id}/configuration`);
      return response.data;
    } catch (error: any) {
      console.error('[workspaceService.getConfiguration] Erro na requisição:', error);
      console.error('[workspaceService.getConfiguration] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  async updateConfiguration(id: string, configuration: any): Promise<void> { // Assuming WorkspaceConfigurationDto is no longer needed
    try {
      await api.put(`/workspaces/${id}/configuration`, configuration);
    } catch (error: any) {
      console.error('[workspaceService.updateConfiguration] Erro na requisição:', error);
      console.error('[workspaceService.updateConfiguration] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  async getProviderSettings(id: string, provider: string): Promise<Record<string, any>> {
    try {
      const response = await api.get(`/workspaces/${id}/configuration/providers/${provider}`);
      return response.data;
    } catch (error: any) {
      console.error('[workspaceService.getProviderSettings] Erro na requisição:', error);
      console.error('[workspaceService.getProviderSettings] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  async updateProviderSetting<T>(id: string, provider: string, key: string, value: T): Promise<void> {
    try {
      await api.put(`/workspaces/${id}/configuration/providers/${provider}/${key}`, { value });
    } catch (error: any) {
      console.error('[workspaceService.updateProviderSetting] Erro na requisição:', error);
      console.error('[workspaceService.updateProviderSetting] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  async resetConfigurationToDefaults(id: string): Promise<void> {
    try {
      await api.post(`/workspaces/${id}/configuration/reset`);
    } catch (error: any) {
      console.error('[workspaceService.resetConfigurationToDefaults] Erro na requisição:', error);
      console.error('[workspaceService.resetConfigurationToDefaults] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  async validateConfiguration(id: string): Promise<{ isValid: boolean; errors: string[] }> {
    try {
      const response = await api.get(`/workspaces/${id}/configuration/validate`);
      return response.data;
    } catch (error: any) {
      console.error('[workspaceService.validateConfiguration] Erro na requisição:', error);
      console.error('[workspaceService.validateConfiguration] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }

  // Método para avaliar mensagem do chat
  async evaluateChatMessage(chatId: string, evaluation: boolean | null): Promise<void> {
    try {
      await api.put('/workspaces/chats/evaluation', {
        id: chatId,
        evaluation: evaluation
      });
    } catch (error: any) {
      console.error('[workspaceService.evaluateChatMessage] Erro na requisição:', error);
      console.error('[workspaceService.evaluateChatMessage] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  }
}

export const workspaceService = new WorkspaceService();
